import { getCommonStockInstantBuybackThreshold } from "./gacha-series"
import type { GachaDisclaimerAcceptanceStatus } from "./use-gacha-disclaimer-acceptance"

export function shouldRequireInstantBuybackForLowCommonStock({
  machineCode,
  commonStock,
  purchaseQuantity,
  instantBuybackEnabled,
}: {
  machineCode?: string
  commonStock: number
  purchaseQuantity: number
  instantBuybackEnabled: boolean
}) {
  if (instantBuybackEnabled) return false

  return (
    commonStock - purchaseQuantity <
    getCommonStockInstantBuybackThreshold(machineCode) - 1
  )
}

export function shouldPreventGachaDialogDismiss({
  isPrivyModalOpen,
  isPrivyPopupInteraction,
  purchaseBusy,
}: {
  isPrivyModalOpen?: boolean
  isPrivyPopupInteraction?: boolean
  purchaseBusy: boolean
}) {
  return purchaseBusy || Boolean(isPrivyModalOpen || isPrivyPopupInteraction)
}

export function shouldOpenGachaPurchaseDialog({
  open,
  resultOpen,
  revealOpen,
  autoPurchase,
}: {
  open: boolean
  resultOpen: boolean
  revealOpen: boolean
  autoPurchase: boolean
}) {
  return open && !resultOpen && !revealOpen && !autoPurchase
}

export function shouldUseModalGachaPurchaseDialog({
  isPrivyModalOpen,
}: {
  isPrivyModalOpen: boolean
}) {
  return !isPrivyModalOpen
}

export function isLikelyPrivyPopupElement(target: EventTarget | null) {
  if (!(target instanceof Element)) return false

  return Boolean(
    target.closest(
      [
        "[data-privy]",
        "[data-privy-modal]",
        "[data-testid*='privy' i]",
        "[id*='privy' i]",
        "[class*='privy' i]",
        "iframe[src*='auth.privy.io']",
        "iframe[src*='privy.io']",
      ].join(","),
    ),
  )
}

export type GachaDisclaimerPurchaseGateAction =
  | {
      action: "purchase"
    }
  | {
      action: "show-disclaimer"
      errorMessage?: string
    }

export type GachaDisclaimerGateAction =
  | {
      action: "proceed"
    }
  | {
      action: "show-disclaimer"
      errorMessage?: string
    }

export function getGachaDisclaimerGateAction({
  cachedStatus,
  checkedAccepted,
  checkErrorMessage,
}: {
  cachedStatus: GachaDisclaimerAcceptanceStatus
  checkedAccepted?: boolean
  checkErrorMessage?: string
}): GachaDisclaimerGateAction {
  if (cachedStatus === "accepted" || checkedAccepted === true) {
    return { action: "proceed" }
  }

  if (checkErrorMessage) {
    return {
      action: "show-disclaimer",
      errorMessage: checkErrorMessage,
    }
  }

  return { action: "show-disclaimer" }
}

export function getGachaDisclaimerPurchaseGateAction({
  cachedStatus,
  checkedAccepted,
  checkErrorMessage,
}: {
  cachedStatus: GachaDisclaimerAcceptanceStatus
  checkedAccepted?: boolean
  checkErrorMessage?: string
}): GachaDisclaimerPurchaseGateAction {
  const gateAction = getGachaDisclaimerGateAction({
    cachedStatus,
    checkedAccepted,
    checkErrorMessage,
  })

  return gateAction.action === "proceed" ? { action: "purchase" } : gateAction
}
